import type {RingApi, RingLookupResult} from '@phonehq/rtc-core';
import logger from '../../../services/logger';
import {ringOutcomes, type RingOutcome, type RingOutcomeStore} from './ringOutcomes';

const MAX_PARALLEL_LOOKUPS = 3;

export const outcomeOfRingResults = (results: readonly RingLookupResult[]): RingOutcome | undefined => {
    const has = (...statuses: RingLookupResult['status'][]) => results.some(result => statuses.includes(result.status));
    if (has('ANSWERED')) {
        return 'answered';
    }
    if (has('DECLINED')) {
        return 'declined';
    }
    if (has('MISSED', 'CANCELLED', 'BUSY')) {
        return 'missed';
    }
    return undefined;
};

export class RingOutcomeLookup {
    private readonly requested = new Map<string, Promise<void>>();
    private readonly waiting: (() => void)[] = [];
    private active = 0;

    constructor(
        private readonly ring: Pick<RingApi, 'findByNotification'>,
        private readonly store: RingOutcomeStore = ringOutcomes,
    ) {}

    resolve(roomId: string, notificationEventId: string): Promise<void> {
        if (this.store.peek(notificationEventId)) {
            return Promise.resolve();
        }
        const existing = this.requested.get(notificationEventId);
        if (existing) {
            return existing;
        }
        const request = this.lookup(roomId, notificationEventId).catch(error => {
            this.requested.delete(notificationEventId);
            logger.warn('Group call ring lookup failed', {
                source: 'group_call',
                event: 'ring_lookup_failed',
                roomId,
                error: error instanceof Error ? error.message : String(error),
            });
        });
        this.requested.set(notificationEventId, request);
        return request;
    }

    private async lookup(roomId: string, notificationEventId: string): Promise<void> {
        await this.acquire();
        try {
            const results = await this.ring.findByNotification(roomId, notificationEventId);
            const outcome = outcomeOfRingResults(results);
            if (outcome) {
                this.store.record(notificationEventId, outcome);
            }
        } finally {
            this.release();
        }
    }

    private acquire(): Promise<void> {
        if (this.active < MAX_PARALLEL_LOOKUPS) {
            this.active += 1;
            return Promise.resolve();
        }
        return new Promise(resolve => this.waiting.push(resolve));
    }

    private release(): void {
        const next = this.waiting.shift();
        if (next) {
            next();
        } else {
            this.active -= 1;
        }
    }
}
